/**
 * Suspicious v2 §4.1 — Person detail "Block this visitor" popover.
 *
 *   Title: "Block this visitor"
 *   Body: "Sill blocks network addresses, not people."
 *   For an IPv6 address:
 *     (•) {ipv6}/64 "home network"
 *          "Recommended: this connection changes its exact address about once a day."
 *     ( ) exact address only
 *   For an IPv4 address: exact address default.
 *   Checkboxes for older addresses are shown when the visitor has
 *     more than one IP.
 *   Duration [7 days ▾]  [Block] [Cancel]
 *
 * The confirms (self / shared / verified-crawler) are returned by the
 * server as 409 codes; the popover shows the inline amber copy and
 * re-submits with `confirm_self: true` on confirm.
 *
 * Role gate: hidden for viewers; also hidden when the IP is hidden
 * (merchant can't see the address, so they can't block it from here).
 */

import { useCallback, useEffect, useState } from 'react';

import type { HttpClient } from '../../api/http-client.js';
import { postManualBlock } from '../../api/threats.js';
import { useC } from '../../lib/theme.js';
import type { ManualBlockDuration, PersonDetailResponse } from '@sill/types/api';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type Mode = { readonly kind: 'host' } | { readonly kind: 'exact' };

export interface PersonBlockPopoverProps {
  readonly siteId: string;
  readonly http: HttpClient;
  readonly personId: string;
  readonly ipAddresses: PersonDetailResponse['ip']['addresses'];
  /** Founder bug 2026-10-18: name the site in the self-block confirm. */
  readonly siteDomain?: string | null;
  readonly onClose: () => void;
  readonly onBlocked: () => void;
}

/** Return the canonical /64 CIDR representation of an IPv6 address. */
function toSlash64(addr: string): string {
  // Expand a shortened address to find the first four hextets.
  try {
    // Fast path: addr already has a ::; use URL via WHATWG to normalise.
    // Fall back to a hand-rolled normaliser.
    const [left, right] = addr.split('::');
    const leftParts = left === undefined ? [] : left.split(':').filter((p) => p.length > 0);
    const rightParts = right === undefined ? [] : right.split(':').filter((p) => p.length > 0);
    const missing = 8 - (leftParts.length + rightParts.length);
    const parts = [
      ...leftParts,
      ...Array(Math.max(missing, 0)).fill('0'),
      ...rightParts,
    ].slice(0, 4);
    const base = parts.map((p) => p || '0').join(':');
    return `${base}::/64`;
  } catch { return `${addr}/64`; }
}

/** Browser-safe IPv6 detection — the dashboard bundle has no node:net. */
function isV6(addr: string): boolean {
  return addr.includes(':') && !addr.includes(' ');
}

export function PersonBlockPopover({
  siteId,
  http,
  personId,
  ipAddresses,
  siteDomain,
  onClose,
  onBlocked,
}: PersonBlockPopoverProps): JSX.Element | null {
  const C = useC();
  const primary = ipAddresses[0];
  const [mode, setMode] = useState<Mode>({ kind: isV6(primary?.address ?? '') ? 'host' : 'exact' });
  const [duration, setDuration] = useState<ManualBlockDuration>('7d');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSelf, setNeedsSelf] = useState(false);
  const [needsShared, setNeedsShared] = useState(false);

  useEffect(() => {
    if (primary === undefined) onClose();
  }, [primary, onClose]);

  const submit = useCallback(async (opts: { confirm_self?: true; confirm_shared?: true } = {}) => {
    if (primary === undefined) return;
    setPending(true);
    setError(null);
    try {
      const base = primary.address;
      const target: { kind: 'ip' | 'cidr'; value: string } = mode.kind === 'host' && isV6(base)
        ? { kind: 'cidr', value: toSlash64(base) }
        : { kind: 'ip', value: base };
      const body: Parameters<typeof postManualBlock>[2] = {
        target,
        duration,
        source: { kind: 'person', ref: personId },
        ...(opts.confirm_self === true ? { confirm_self: true as const } : {}),
        ...(opts.confirm_shared === true ? { confirm_shared: true as const } : {}),
      };
      const res = await postManualBlock(http, siteId, body);
      if (!res.ok) {
        if (res.error === 'self_block_confirm_required') {
          setNeedsSelf(true);
          setError(null);
          return;
        }
        if (res.error === 'shared_network_confirm_required') {
          setNeedsShared(true);
          setError(null);
          return;
        }
        if (res.error === 'verified_crawler') {
          setError('That address overlaps a verified crawler and was not blocked.');
          return;
        }
        if (res.error === 'cdn_or_proxy_range') {
          setError("That address belongs to Cloudflare or a privacy relay used by many real visitors, so Sill won't block it.");
          return;
        }
        if (res.error === 'cloud_ranges_loading') {
          setError('Sill is still loading network data. Try again in a few seconds.');
          return;
        }
        if (res.error === 'cidr_too_broad') {
          setError('That range is too broad. Use /24 or narrower for IPv4, /48 for IPv6.');
          return;
        }
        if (res.error === 'plan_feature_required') {
          // ADR-0263 S4 — the server-supplied plain-copy message names
          // the plan when the catalog has one.
          setError(res.plan_message ?? (res.plan_name !== null && res.plan_name !== undefined
            ? `Blocking is part of the ${res.plan_name} plan.`
            : "Blocking is not on this site's plan."));
          return;
        }
        setError('That block was rejected.');
        return;
      }
      onBlocked();
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setPending(false);
    }
  }, [primary, mode, duration, personId, http, siteId, onBlocked]);

  if (primary === undefined) return null;

  const ipv6 = isV6(primary.address);
  return (
    <div
      data-testid="person-block-popover"
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: 20,
          maxWidth: 420,
          width: '100%',
          fontFamily: sansFont,
        }}
      >
        <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted }}>BLOCK</div>
        <h3 style={{ fontFamily: sansFont, fontSize: 18, margin: '6px 0 4px' }}>Block this visitor</h3>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          Sill blocks network addresses, not people.
        </div>
        {ipv6 ? (
          <fieldset
            data-testid="person-block-mode"
            style={{ border: 'none', padding: 0, margin: '0 0 12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
              <input
                type="radio"
                name="mode"
                data-testid="person-block-mode-host"
                checked={mode.kind === 'host'}
                onChange={() => setMode({ kind: 'host' })}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontFamily: monoFont, fontSize: 12 }}>{toSlash64(primary.address)}</span>
                <span style={{ color: C.muted, marginLeft: 6 }}>home network</span>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  Recommended: this connection changes its exact address about once a day.
                </div>
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="radio"
                name="mode"
                data-testid="person-block-mode-exact"
                checked={mode.kind === 'exact'}
                onChange={() => setMode({ kind: 'exact' })}
              />
              <span>
                <span style={{ fontFamily: monoFont, fontSize: 12 }}>{primary.address}</span>
                <span style={{ color: C.muted, marginLeft: 6 }}>exact address only</span>
              </span>
            </label>
          </fieldset>
        ) : (
          <div data-testid="person-block-mode-ipv4" style={{ fontSize: 13, marginBottom: 12 }}>
            <span style={{ fontFamily: monoFont, fontSize: 12 }}>{primary.address}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 }}>
          <span style={{ color: C.muted }}>Duration</span>
          <select
            data-testid="person-block-duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value as ManualBlockDuration)}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              padding: '4px 6px',
              background: C.surface,
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <option value="24h">24 hours</option>
            <option value="7d">7 days</option>
            <option value="forever">Forever</option>
          </select>
        </div>
        {needsSelf ? (
          <ConfirmBanner
            kind="self"
            body={(() => {
              const d = siteDomain ?? 'this site';
              return `This is your current address. When blocking is on for ${d}, you won't be able to open ${d} from this network. The Sill dashboard and your other sites keep working.`;
            })()}
            onConfirm={() => { setNeedsSelf(false); void submit({ confirm_self: true }); }}
            onCancel={() => setNeedsSelf(false)}
          />
        ) : null}
        {needsShared ? (
          <ConfirmBanner
            kind="shared"
            body={(() => {
              const d = siteDomain ?? 'this site';
              return `${primary.address} is on a home or mobile network and may be shared by other people. Blocking it forever on ${d} will stop everyone on that network from reaching ${d}. Sill recommends 24 hours.`;
            })()}
            onConfirm={() => { setNeedsShared(false); void submit({ confirm_shared: true }); }}
            onCancel={() => { setNeedsShared(false); setDuration('24h'); void submit(); }}
          />
        ) : null}
        {error !== null ? (
          <div data-testid="person-block-error" style={{ fontSize: 12, color: C.coralFg, marginBottom: 8 }}>{error}</div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              padding: '6px 10px',
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={pending}
            data-testid="person-block-submit"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              padding: '6px 12px',
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRadius: 2,
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            Block
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmBanner({
  kind, body, onConfirm, onCancel,
}: {
  readonly kind: 'self' | 'shared';
  readonly body: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      data-testid="person-block-confirm"
      data-kind={kind}
      style={{
        padding: '8px 10px',
        border: `1px solid ${C.amberFg}`,
        borderRadius: 2,
        marginBottom: 8,
        fontSize: 12,
        color: C.text,
      }}
    >
      <div style={{ marginBottom: 6 }}>{body}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="person-block-confirm-primary"
          style={{
            fontFamily: sansFont, fontSize: 12,
            padding: '4px 8px',
            background: C.lime, color: C.ink,
            border: `1px solid ${C.lime}`, borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          {kind === 'self' ? 'Block anyway' : 'Block forever anyway'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="person-block-confirm-secondary"
          style={{
            fontFamily: sansFont, fontSize: 12,
            padding: '4px 8px',
            background: 'transparent', color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          {kind === 'self' ? 'Cancel' : 'Block for 24 hours'}
        </button>
      </div>
    </div>
  );
}
